import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./ProfitLossReport.css";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Login from "../../Authentication/Login";
import AccountingReportTabs from "./AccoutingReportTabs";
import Header from "../../Common/Layout/Header";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import moment from "moment";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const ProfitLoss = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [expandedRows, setExpandedRows] = useState({});
  const [companyData, setCompanyData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [reportData, setReportData] = useState({
    income: [],
    expenses: [],
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
  });

  // Get user's currency from auth context
  const getUserCurrency = () => {
    return user?.currency || "INR"; // Fallback to INR if currency is not available
  };

  // Get branches for the dropdown
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const GetCompanyData = async () => {
    try {
      const res = await apiService.get(`${config.GETCOMPANYBYUSER}/${user.id}`);
      setCompanyData(res);
    } catch (err) {
      console.error("Error fetching company data:", err);
    }
  };

  const GetProfitLossAccountWiseReport = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      if (fromDate)
        params.append("FromDate", moment(fromDate).format("YYYY-MM-DD"));
      if (toDate) params.append("ToDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.PROFITLOSSACCOUNTWISEREPORT}/${user.id}?${params.toString()}`
      );

      console.log("Raw API Response:", res);
      console.log(
        "Response type names:",
        res.map((item) => item.typeName)
      );

      const processedData = processReportData(res);
      console.log("Processed Data:", processedData);

      setReportData(
        processedData || {
          income: [],
          expenses: [],
          totalIncome: 0,
          totalExpenses: 0,
          netProfit: 0,
        }
      );
    } catch (err) {
      console.error("Error fetching profit/loss report:", err);
      setReportData({
        income: [],
        expenses: [],
        totalIncome: 0,
        totalExpenses: 0,
        netProfit: 0,
      });
    }
  };

  const processReportData = (data) => {
    if (!data || data.length === 0) return null;

    const result = {
      income: [],
      expenses: [],
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
    };

    const groupedData = data.reduce((acc, item) => {
      // Handle both "EXPENSES" and "EXPENSE" variations
      const typeKey =
        item.typeName.toLowerCase() === "expenses"
          ? "expenses"
          : item.typeName.toLowerCase();

      if (!acc[typeKey]) acc[typeKey] = {};

      if (!acc[typeKey][item.accountName]) {
        acc[typeKey][item.accountName] = {
          accountName: item.accountName,
          totalAmount: 0,
          items: [],
        };
      }

      acc[typeKey][item.accountName].items.push({
        itemName: item.itemName,
        invoiceDate: item.invoiceDate,
        totalAmount: item.totalAmount,
      });

      acc[typeKey][item.accountName].totalAmount += item.totalAmount;
      return acc;
    }, {});

    console.log("Grouped Data:", groupedData); // Debug log

    if (groupedData.income) {
      result.income = Object.values(groupedData.income);
      result.totalIncome = result.income.reduce(
        (sum, account) => sum + account.totalAmount,
        0
      );
    }

    if (groupedData.expenses) {
      result.expenses = Object.values(groupedData.expenses);
      result.totalExpenses = result.expenses.reduce(
        (sum, account) => sum + account.totalAmount,
        0
      );
    }

    result.netProfit = result.totalIncome - result.totalExpenses;

    return result;
  };

  // useEffect(() => {
  //   if (user && !loading) {
  //     GetCompanyData();
  //     GetBranches();
  //   }
  // }, [user, loading]);

  useEffect(() => {
    if (user && !loading) {
      GetCompanyData();
      GetBranches();
      GetProfitLossAccountWiseReport();
    }
  }, [user, loading, fromDate, toDate, selectedBranch]);

  const handleSearch = (e) => {
    e.preventDefault();
    GetProfitLossAccountWiseReport();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setSelectedBranch("all");
    setReportData({
      income: [],
      expenses: [],
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
    });
    setExpandedRows({});
  };

  const toggleRow = (itemType, itemName) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${itemType}-${itemName}`]: !prev[`${itemType}-${itemName}`],
    }));
  };

  const exportToPDF = () => {
    const currency = getUserCurrency();
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set styles for the document
    doc.setFont("helvetica");
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.setFont(undefined, "bold");

    // Title - centered
    doc.text("PROFIT & LOSS STATEMENT", 105, 20, { align: "center" });

    // Company details - centered
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");

    // Company name
    doc.text(companyData?.companyName || "Company", 105, 30, {
      align: "center",
    });

    // Branch information if selected
    if (selectedBranch && selectedBranch !== "all") {
      const branchName =
        branches.find((b) => b.branchId === selectedBranch)?.branchName ||
        selectedBranch;
      doc.text(`Branch: ${branchName}`, 105, 35, { align: "center" });
    }

    // Address
    const addressLine = `${companyData?.address || ""}, ${
      companyData?.city || ""
    }, ${companyData?.state || ""}, ${companyData?.country || ""}`;
    doc.text(
      addressLine,
      105,
      selectedBranch && selectedBranch !== "all" ? 40 : 35,
      { align: "center" }
    );

    // Contact info
    const contactLine = `Phone: ${companyData?.phoneNumber || ""} | Email: ${
      companyData?.email || ""
    }`;
    doc.text(
      contactLine,
      105,
      selectedBranch && selectedBranch !== "all" ? 45 : 40,
      { align: "center" }
    );

    // Report period - centered
    doc.text(
      `For the period from ${
        fromDate ? moment(fromDate).format("YYYY-MM-DD") : ""
      } to ${toDate ? moment(toDate).format("YYYY-MM-DD") : ""}`,
      105,
      selectedBranch && selectedBranch !== "all" ? 50 : 45,
      { align: "center" }
    );

    // Generated date - centered
    doc.text(
      `Generated on: ${moment().format("YYYY-MM-DD hh:mm A")}`,
      105,
      selectedBranch && selectedBranch !== "all" ? 55 : 50,
      { align: "center" }
    );

    // Income Section
    doc.setFontSize(12);
    doc.setTextColor(41, 128, 185);
    doc.setFont(undefined, "bold");
    doc.text(
      "Income",
      14,
      selectedBranch && selectedBranch !== "all" ? 65 : 60
    );

    // Income Table
    doc.autoTable({
      startY: selectedBranch && selectedBranch !== "all" ? 70 : 65,
      head: [["Account", `Amount (${currency})`]],
      body: reportData.income.map((account) => [
        account.accountName,
        `${currency} ${account.totalAmount.toFixed(2)}`,
      ]),
      foot: [
        ["Total Income", `${currency} ${reportData.totalIncome.toFixed(2)}`],
      ],
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      bodyStyles: {
        textColor: 0,
        fontStyle: "normal",
      },
      footStyles: {
        fontStyle: "bold",
        textColor: 0,
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: 120, halign: "left" },
        1: { cellWidth: 60, halign: "right" },
      },
      margin: { left: 14 },
    });

    // Expenses Section
    doc.setFontSize(12);
    doc.setTextColor(41, 128, 185);
    doc.setFont(undefined, "bold");
    doc.text("Expenses", 14, doc.lastAutoTable.finalY + 10);

    // Expenses Table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 15,
      head: [["Account", `Amount (${currency})`]],
      body: reportData.expenses.map((account) => [
        account.accountName,
        `${currency} ${account.totalAmount.toFixed(2)}`,
      ]),
      foot: [
        [
          "Total Expenses",
          `${currency} ${reportData.totalExpenses.toFixed(2)}`,
        ],
      ],
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      bodyStyles: {
        textColor: 0,
        fontStyle: "normal",
      },
      footStyles: {
        fontStyle: "bold",
        textColor: 0,
      },
      styles: {
        fontSize: 10,
        cellPadding: 3,
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: 120, halign: "left" },
        1: { cellWidth: 60, halign: "right" },
      },
      margin: { left: 14 },
    });

    // Net Profit/Loss Section
    const profitLoss = reportData.netProfit;
    const isProfit = profitLoss >= 0;

    doc.setFontSize(14);
    doc.setTextColor(
      isProfit ? 46 : 198,
      isProfit ? 125 : 40,
      isProfit ? 50 : 40
    );
    doc.setFillColor(
      isProfit ? 232 : 255,
      isProfit ? 245 : 235,
      isProfit ? 233 : 238
    );
    doc.rect(14, doc.lastAutoTable.finalY + 15, 180, 10, "F");
    doc.text(
      `${isProfit ? "Net Profit" : "Net Loss"}: ${currency} ${Math.abs(
        profitLoss
      ).toFixed(2)}`,
      105,
      doc.lastAutoTable.finalY + 20,
      { align: "center" }
    );

    // Footer
    doc.setTextColor(0);
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text(
      `© ${new Date().getFullYear()} ${
        companyData?.companyName || "Company"
      }. All rights reserved.`,
      105,
      285,
      { align: "center" }
    );

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    doc.save("Profit_Loss_Report.pdf");
  };

  const handlePrint = () => {
    const currency = getUserCurrency();
    const printWindow = window.open("", "_blank");
    const branchName =
      selectedBranch && selectedBranch !== "all"
        ? branches.find((b) => b.branchId === selectedBranch)?.branchName ||
          selectedBranch
        : null;

    const printContent = `
      <html>
        <head>
          <title>Profit & Loss Statement</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; text-align: center; }
            .company-info { text-align: center; margin-bottom: 20px; font-size: 14px; }
            .report-period { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background-color: #2980b9; color: white; padding: 8px; text-align: left; }
            td { padding: 6px; border-bottom: 1px solid #ddd; }
            .text-right { text-align: right; }
            .total-row { font-weight: bold; }
            .summary-section { margin-top: 20px; padding: 10px; text-align: center; }
            .profit { background-color: #e8f5e9; color: #2e7d32; }
            .loss { background-color: #ffebee; color: #c62828; }
            .footer { margin-top: 30px; font-size: 12px; text-align: center; }
          </style>
        </head>
        <body>
          <h1>PROFIT & LOSS STATEMENT</h1>
          <div class="company-info">
            <p>${companyData?.companyName || "Company"}</p>
            ${branchName ? `<p><strong>Branch:</strong> ${branchName}</p>` : ""}
            <p>${companyData?.address || ""}, ${companyData?.city || ""}, ${
      companyData?.state || ""
    }, ${companyData?.country || ""}</p>
            <p>Phone: ${companyData?.phoneNumber || ""} | Email: ${
      companyData?.email || ""
    }</p>
          </div>
          
          <div class="report-period">
            <p>For the period from ${
              fromDate ? moment(fromDate).format("YYYY-MM-DD") : ""
            } to ${toDate ? moment(toDate).format("YYYY-MM-DD") : ""}</p>
          </div>

          <h2>Income</h2>
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th class="text-right">Amount (${currency})</th>
              </tr>
            </thead>
            <tbody>
              ${
                reportData.income.length > 0
                  ? reportData.income
                      .map(
                        (account) => `
                  <tr>
                    <td>${account.accountName}</td>
                    <td class="text-right">${currency} ${account.totalAmount.toFixed(
                          2
                        )}</td>
                  </tr>
                `
                      )
                      .join("")
                  : `
                  <tr>
                    <td colspan="2" style="text-align: center; color: #666;">No income data available</td>
                  </tr>
                `
              }
              <tr class="total-row">
                <td>Total Income</td>
                <td class="text-right">${currency} ${reportData.totalIncome.toFixed(
      2
    )}</td>
              </tr>
            </tbody>
          </table>

          <h2>Expenses</h2>
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th class="text-right">Amount (${currency})</th>
              </tr>
            </thead>
            <tbody>
              ${
                reportData.expenses.length > 0
                  ? reportData.expenses
                      .map(
                        (account) => `
                  <tr>
                    <td>${account.accountName}</td>
                    <td class="text-right">${currency} ${account.totalAmount.toFixed(
                          2
                        )}</td>
                  </tr>
                `
                      )
                      .join("")
                  : `
                  <tr>
                    <td colspan="2" style="text-align: center; color: #666;">No expense data available</td>
                  </tr>
                `
              }
              <tr class="total-row">
                <td>Total Expenses</td>
                <td class="text-right">${currency} ${reportData.totalExpenses.toFixed(
      2
    )}</td>
              </tr>
            </tbody>
          </table>

          <div class="summary-section ${
            (reportData?.netProfit || 0) >= 0 ? "profit" : "loss"
          }">
            <h3>${
              (reportData?.netProfit || 0) >= 0 ? "Net Profit" : "Net Loss"
            }</h3>
            <p>${currency} ${Math.abs(reportData?.netProfit || 0).toFixed(
      2
    )}</p>
          </div>

          <div class="footer">
            <p>This report was generated automatically on ${moment().format(
              "YYYY-MM-DD"
            )}.</p>
            <p>© ${new Date().getFullYear()} ${
      companyData?.companyName || "Company"
    }. All rights reserved.</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 200);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <AccountingReportTabs pageName="PROFITLOSSREPORT" />
                </div>
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-end flex-wrap">
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">Branch:</label>
                          <select
                            className="form-select"
                            value={selectedBranch}
                            onChange={(e) => setSelectedBranch(e.target.value)}
                          >
                            <option value="all">All Branches</option>
                            {branches.map((branch) => (
                              <option
                                key={branch.branchId}
                                value={branch.branchId}
                              >
                                {branch.branchName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">From Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => setFromDate(date)}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select from date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">To Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              minDate={fromDate}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select to date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="d-flex mb-2">
                          <Button
                            variant="primary"
                            className="me-2"
                            onClick={handleSearch}
                          >
                            Search
                          </Button>
                          <Button variant="danger" onClick={handleClear}>
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 d-flex justify-content-end">
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle variant="primary" id="dropdown-export">
                          <i className="ri-download-line me-1"></i>
                          Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={exportToPDF}>
                            <i className="ri-file-pdf-line me-2 text-danger"></i>
                            PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        variant="secondary"
                        onClick={handlePrint}
                        className="ms-2"
                      >
                        <i className="ri-printer-line me-1"></i>
                        Print
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="report-container">
                    <div className="header-section">
                      <h1 className="company-name">
                        {companyData?.companyName || "Company"}
                      </h1>
                      {selectedBranch && selectedBranch !== "all" && (
                        <p className="branch-info">
                          <strong>Branch:</strong>{" "}
                          {branches.find((b) => b.branchId === selectedBranch)
                            ?.branchName || selectedBranch}
                        </p>
                      )}
                      <p className="company-info">
                        {companyData?.address || ""}, {companyData?.city || ""},{" "}
                        {companyData?.state || ""}, {companyData?.country || ""}
                        <br />
                        Phone: {companyData?.phoneNumber || ""} | Email:{" "}
                        {companyData?.email || ""}
                      </p>
                    </div>

                    <div className="report-title">
                      <h2>Profit & Loss Statement</h2>
                      <p className="report-period">
                        For the period from{" "}
                        {fromDate ? moment(fromDate).format("YYYY-MM-DD") : ""}{" "}
                        to {toDate ? moment(toDate).format("YYYY-MM-DD") : ""}
                      </p>
                    </div>

                    {/* Income Section */}
                    <div className="financial-section income-section">
                      <h3>Income</h3>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead className="bg-soft-success bg-gradient">
                            <tr>
                              <th className="expand-col"></th>
                              <th className="item-col">Account</th>
                              <th className="text-end">
                                Amount ({getUserCurrency()})
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.income.length > 0 ? (
                              reportData.income.map((account, idx) => {
                                const isExpanded =
                                  expandedRows[`income-${account.accountName}`];
                                return (
                                  <React.Fragment key={`income-${idx}`}>
                                    <tr
                                      className={`data-row ${
                                        isExpanded ? "expanded" : ""
                                      }`}
                                      onClick={() =>
                                        toggleRow("income", account.accountName)
                                      }
                                    >
                                      <td className="expand-col">
                                        {account.items?.length > 0
                                          ? isExpanded
                                            ? "▼"
                                            : "►"
                                          : ""}
                                      </td>
                                      <td className="item-col">
                                        {account.accountName}
                                      </td>
                                      <td className="text-end">
                                        {getUserCurrency()}{" "}
                                        {account.totalAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                    {isExpanded &&
                                      account.items?.length > 0 && (
                                        <tr className="details-row">
                                          <td colSpan="3">
                                            <div className="details-container">
                                              <table className="table table-bordered">
                                                <thead>
                                                  <tr>
                                                    <th>Date</th>
                                                    <th>Item</th>
                                                    <th className="text-end">
                                                      Amount (
                                                      {getUserCurrency()})
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {account.items.map(
                                                    (item, itemIdx) => (
                                                      <tr
                                                        key={`income-item-${itemIdx}`}
                                                      >
                                                        <td>
                                                          {moment(
                                                            item.invoiceDate
                                                          ).format(
                                                            "YYYY-MM-DD"
                                                          )}
                                                        </td>
                                                        <td>{item.itemName}</td>
                                                        <td className="text-end">
                                                          {getUserCurrency()}{" "}
                                                          {item.totalAmount.toFixed(
                                                            2
                                                          )}
                                                        </td>
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan="3"
                                  className="text-center text-muted"
                                >
                                  No income data available
                                </td>
                              </tr>
                            )}
                            <tr className="total-row">
                              <td colSpan="2">Total Income</td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {reportData.totalIncome.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Expenses Section */}
                    <div className="financial-section expenses-section">
                      <h3>Expenses</h3>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover mb-0">
                          <thead className="bg-soft-success bg-gradient">
                            <tr>
                              <th className="expand-col"></th>
                              <th className="item-col">Account</th>
                              <th className="text-end">
                                Amount ({getUserCurrency()})
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.expenses.length > 0 ? (
                              reportData.expenses.map((account, idx) => {
                                const isExpanded =
                                  expandedRows[
                                    `expenses-${account.accountName}`
                                  ];
                                return (
                                  <React.Fragment key={`expense-${idx}`}>
                                    <tr
                                      className={`data-row ${
                                        isExpanded ? "expanded" : ""
                                      }`}
                                      onClick={() =>
                                        toggleRow(
                                          "expenses",
                                          account.accountName
                                        )
                                      }
                                    >
                                      <td className="expand-col">
                                        {account.items?.length > 0
                                          ? isExpanded
                                            ? "▼"
                                            : "►"
                                          : ""}
                                      </td>
                                      <td className="item-col">
                                        {account.accountName}
                                      </td>
                                      <td className="text-end">
                                        {getUserCurrency()}{" "}
                                        {account.totalAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                    {isExpanded &&
                                      account.items?.length > 0 && (
                                        <tr className="details-row">
                                          <td colSpan="3">
                                            <div className="details-container">
                                              <table className="table table-bordered">
                                                <thead>
                                                  <tr>
                                                    <th>Date</th>
                                                    <th>Item</th>
                                                    <th className="text-end">
                                                      Amount (
                                                      {getUserCurrency()})
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {account.items.map(
                                                    (item, itemIdx) => (
                                                      <tr
                                                        key={`expense-item-${itemIdx}`}
                                                      >
                                                        <td>
                                                          {moment(
                                                            item.invoiceDate
                                                          ).format(
                                                            "YYYY-MM-DD"
                                                          )}
                                                        </td>
                                                        <td>{item.itemName}</td>
                                                        <td className="text-end">
                                                          {getUserCurrency()}{" "}
                                                          {item.totalAmount.toFixed(
                                                            2
                                                          )}
                                                        </td>
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan="3"
                                  className="text-center text-muted"
                                >
                                  No expense data available
                                </td>
                              </tr>
                            )}
                            <tr className="total-row">
                              <td colSpan="2">Total Expenses</td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {reportData.totalExpenses.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      className={`summary-section ${
                        reportData.netProfit >= 0 ? "profit" : "loss"
                      }`}
                    >
                      <h3>
                        {reportData.netProfit >= 0 ? "Net Profit" : "Net Loss"}
                      </h3>
                      <p className="summary-amount">
                        {getUserCurrency()}{" "}
                        {Math.abs(reportData.netProfit).toFixed(2)}
                      </p>
                    </div>

                    <div className="report-footer">
                      <p>
                        This report was generated automatically on{" "}
                        {moment().format("YYYY-MM-DD")}.
                      </p>
                      <p>
                        © {new Date().getFullYear()}{" "}
                        {companyData?.companyName || "Company"}. All rights
                        reserved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLoss;
